import React, { useState, useEffect } from "react";
import Select from "react-select";
import {
  CustomerName,
  OrderStatus,
  PaymentType,
  Supplier,
} from "../../../core/common/selectOption/selectOption";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Minus,
  PlusCircle,
} from "feather-icons-react/build/IconComponents";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import DefaultEditor from "react-simple-wysiwyg";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { fetchSuppliers } from "../../../api/supplier";
import { getAllCustomers } from "../../../api/customerApi";
import { fetchProductList } from "../../../api/productApi";
import {
  createSalesOrder,
  getSalesOrderById,
} from "../../../api/salesOrderApi";
import "../online-order/onlineSales.css";

export const openDetailsModal = async (orderId, setSalesDetails) => {
  try {
    const order = await getSalesOrderById(orderId);
    setSalesDetails(order);
    // Don’t call $(...).modal("show") if you already use data-bs-* to open it
  } catch (err) {
    console.error("openDetailsModal error:", err);
    // toast.error(err?.message || "Failed to fetch sales details");
    toast.error(`❌ ${window.translate("Failed to fetch sales details")}`);
  }
};

const OnlineorderModal = ({ salesDetails }) => {
  const [customerOptions, setCustomerOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [productList, setProductList] = useState([]);
  // const [salesDetails] = useState(null);
  const [formData, setFormData] = useState({
    customer: "",
    supplier: "",
    reference: `SALE-${Date.now()}`,
    date: new Date(),
    items: [],
    total: 0,
    paid: 0,
    status: "",
    paymentStatus: "Unpaid",
    orderTax: 0,
    discount: 0,
    shipping: 0,
    grandTotal: 0,
  });

  // === Row math helpers ===
  const TAX_PERCENT = 10;

  const recomputeFromItems = (items, prev) => {
    let total = 0,
      totalTax = 0,
      totalDiscount = 0;

    items.forEach((item) => {
      const price = parseFloat(item.price || 0);
      const qty = parseInt(item.quantity || 1);
      const dVal = parseFloat(item.discountValue || 0);
      const dType = item.discountType || "flat";
      const taxType = item.taxType || "exclusive";

      const discountAmt =
        dType === "percentage" ? (price * qty * dVal) / 100 : dVal * qty;

      totalDiscount += discountAmt;

      const taxable = price * qty - discountAmt;
      const taxRate = taxType === "exclusive" ? TAX_PERCENT : 0;
      const taxAmt = (taxable * taxRate) / 100;

      totalTax += taxAmt;
      total += taxable + taxAmt;
    });

    const updated = {
      ...prev,
      items,
      total: total.toFixed(2),
      orderTax: totalTax.toFixed(2),
      discount: totalDiscount.toFixed(2),
    };
    return { ...updated, grandTotal: recalculateTotals(updated) };
  };

  const updateItem = (idx, patch) => {
    setFormData((prev) => {
      const items = prev.items.map((it, i) =>
        i === idx ? { ...it, ...patch } : it
      );
      return recomputeFromItems(items, prev);
    });
  };

  const removeItem = (idx) => {
    setFormData((prev) => {
      const items = prev.items.filter((_, i) => i !== idx);
      return recomputeFromItems(items, prev);
    });
  };

  //   get customer, supplier, products
  useEffect(() => {
    loadFormOptions();
  }, []);

  const recalculateTotals = (updatedData = formData) => {
    const total = parseFloat(updatedData.total) || 0;
    const orderTax = parseFloat(updatedData.orderTax) || 0;
    const discount = parseFloat(updatedData.discount) || 0;
    const shipping = parseFloat(updatedData.shipping) || 0;

    const grandTotal = total + orderTax + shipping - discount;

    return grandTotal.toFixed(2);
  };

  const roundToTwo = (num) =>
    Math.round((parseFloat(num) + Number.EPSILON) * 100) / 100;

  const loadFormOptions = async () => {
    try {
      // Fetch customers
      const customers = await getAllCustomers();
      setCustomerOptions(
        customers.map((c) => ({
          value: c.id,
          label: `${c.CustomerName?.en || ""} ${c.LastName?.en || ""}`,
        }))
      );

      // Fetch suppliers
      const suppliers = await fetchSuppliers();
      setSupplierOptions(
        suppliers.map((s) => ({
          value: s.id,
          label: `${s.firstName?.en || ""} ${s.lastName?.en || ""}`,
        }))
      );

      // Fetch products
      const products = await fetchProductList();
      setProductList(
        products.map((p) => ({
          value: p.id,
          label: `${
            p?.Product_Name?.en || p?.Product_Name || "Unnamed Product"
          } - ${p?.sku || p?.SKU || "No SKU"}`,
          fullProduct: p, // ✅ Attach full product
        }))
      );
    } catch (err) {
      console.error("Error loading form options", err);
      // toast.error("Failed to load customers, suppliers, or products");
      toast.error(`❌ ${window.translate("Failed to load customers, suppliers, or products")}`);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddProduct = (productObj) => {
    if (!productObj || !productObj.price || !productObj.quantity) {
      // toast.error("Invalid product selected or missing price/quantity");
      toast.error(`❌ ${window.translate("Invalid product selected or missing price/quantity")}`);
      return;
    }

    setFormData((prev) => {
      // if same product+variant exists, just increase qty
      const i = prev.items.findIndex(
        (x) =>
          x.product === productObj.product && x.variant === productObj.variant
      );
      const items = [...prev.items];
      if (i >= 0) {
        const currQty = parseInt(items[i].quantity || 1);
        items[i] = { ...items[i], quantity: currQty + 1 };
      } else {
        items.push(productObj);
      }
      return recomputeFromItems(items, prev);
    });
  };

  const [quantity, setQuantity] = useState(4);

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleIncrement = () => {
    setQuantity(quantity + 1);
  };

  const [values, setValue] = useState();
  function onChange(e) {
    setValue(e.target.value);
  }

  const handleSubmit = async () => {
    console.log("Submitting FormData:", formData);

    if (
      !formData.customer ||
      !formData.supplier ||
      !formData.items ||
      formData.items.length === 0
    ) {
      console.log("Validation Failed: Items =", formData.items);
      // toast.error("Please fill required fields and add at least one product");
       toast.error(`❌ ${window.translate("Please fill required fields and add at least one product")}`);
      return;
    }
    try {
      const roundedFormData = {
        ...formData,
        // total: roundToTwo(formData.total),
        total: roundToTwo(formData.grandTotal),
        orderTax: roundToTwo(formData.orderTax),
        discount: roundToTwo(formData.discount),
        shipping: roundToTwo(formData.shipping),
        grandTotal: roundToTwo(formData.grandTotal),
        paid: roundToTwo(formData.paid),
      };
      const data = await createSalesOrder(roundedFormData);
      // toast.success("Sales order submitted successfully");
      toast.success(` ${window.translate("Sales order submitted successfully")}`);
      console.log("Created Order:", data);

      // Reset form
      setFormData({
        customer: "",
        supplier: "",
        reference: `SALE-${Date.now()}`,
        date: new Date(),
        items: [],
        total: 0,
        paid: 0,
        status: "",
        paymentStatus: "Unpaid",
        orderTax: 0,
        discount: 0,
        shipping: 0,
        grandTotal: 0,
      });
    } catch (error) {
      // toast.error(error.message || "Failed to submit sales order");
      toast.error(`❌ ${window.translate("Failed to submit sales order")}`);
    }
  };

  const getProductName = (product) => {
    if (!product) return "Unnamed";
    return (
      product?.productName?.en ?? // your payload
      product?.Product_Name?.en ?? // older shape fallback
      product?.productName ??
      product?.Product_Name ??
      product?.name ??
      product?.slug?.en ??
      product?.slug ??
      "Unnamed"
    );
  };

  const getProductImage = (product) => {
    if (!product) return "assets/img/products/stock-img-02.png";

    const imgs = product?.images;

    // If it's an array, use the first; if it's a string (URL or base64), use it
    if (Array.isArray(imgs) && imgs.length) return imgs[0];
    if (typeof imgs === "string" && imgs.length) return imgs;

    // Other possible single-field fallbacks
    if (product?.image) return product.image;
    if (product?.imageUrl) return product.imageUrl;

    return "assets/img/products/stock-img-02.png";
  };

  // Render <img> directly for base64 data URLs; otherwise use your wrapper
  const ProductImage = ({ src, alt }) => {
    const isDataUrl = typeof src === "string" && src.startsWith("data:");
    return isDataUrl ? (
      <img src={src} alt={alt} className="img-fluid" />
    ) : (
      <ImageWithBasePath src={src} alt={alt} />
    );
  };

  ProductImage.propTypes = {
    src: PropTypes.string, // or PropTypes.string.isRequired
    alt: PropTypes.string,
  };

  ProductImage.defaultProps = {
    src: "assets/img/products/stock-img-02.png",
    alt: "product",
  };

  return (
    <div>
      <>
        {/*add popup */}
        <div className="modal fade" id="add-sales-new">
          <div className="modal-dialog add-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div className="page-title">
                  <h4> Add sSales</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <form>
                <div className="card border-0">
                  <div className="card-body pb-0">
                    <div className="sales-table table-responsive mb-3">
                      <table className="table table-sm align-middle table-hover table-editable">
                        <thead className="table-light">
                          <tr>
                            <th className="col-product">Product</th>
                            <th className="col-qty text-center">Qty</th>
                            <th className="col-price text-end">
                              Purchase Price($)
                            </th>
                            <th className="col-discount">Discount($ / %)</th>
                            <th className="col-tax">Tax(%)</th>
                            <th className="col-money text-end">
                              Tax Amount($)
                            </th>
                            <th className="col-money text-end">Unit Cost($)</th>
                            <th className="col-money text-end">
                              Total Cost($)
                            </th>
                            <th className="col-actions text-center"> </th>
                          </tr>
                        </thead>

                        <tbody>
                          {formData.items.length === 0 ? (
                            <tr>
                              <td
                                colSpan={9}
                                className="text-center text-muted py-4"
                              >
                                No products added
                              </td>
                            </tr>
                          ) : (
                            formData.items.map((it, idx) => {
                              const price = parseFloat(it.price || 0);
                              const qty = parseInt(it.quantity || 1);
                              const dVal = parseFloat(it.discountValue || 0);
                              const dType = it.discountType || "flat";
                              const taxType = it.taxType || "exclusive";
                              const taxRate =
                                taxType === "exclusive" ? TAX_PERCENT : 0;

                              const discountAmt =
                                dType === "percentage"
                                  ? (price * qty * dVal) / 100
                                  : dVal * qty;
                              const taxable = price * qty - discountAmt;
                              const taxAmt = (taxable * taxRate) / 100;
                              const totalCost = taxable + taxAmt;

                              const label =
                                productList.find((p) => p.value === it.product)
                                  ?.label || it.product;

                              return (
                                <tr key={idx}>
                                  <td className="text-truncate" title={label}>
                                    {label}
                                  </td>

                                  <td className="col-qty">
                                    <input
                                      type="number"
                                      min={1}
                                      value={qty}
                                      onChange={(e) =>
                                        updateItem(idx, {
                                          quantity: parseInt(
                                            e.target.value || 1
                                          ),
                                        })
                                      }
                                      className="form-control form-control-sm text-end"
                                    />
                                  </td>

                                  <td className="col-price">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={price}
                                      onChange={(e) =>
                                        updateItem(idx, {
                                          price: parseFloat(
                                            e.target.value || 0
                                          ),
                                        })
                                      }
                                      className="form-control form-control-sm text-end"
                                    />
                                  </td>

                                  <td className="col-discount">
                                    <div className="input-group input-group-sm">
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={dVal}
                                        onChange={(e) =>
                                          updateItem(idx, {
                                            discountValue: parseFloat(
                                              e.target.value || 0
                                            ),
                                          })
                                        }
                                        className="form-control text-end"
                                      />
                                      <select
                                        className="form-select"
                                        value={dType}
                                        onChange={(e) =>
                                          updateItem(idx, {
                                            discountType: e.target.value,
                                          })
                                        }
                                        style={{ maxWidth: 80 }}
                                      >
                                        <option value="flat">$</option>
                                        <option value="percentage">%</option>
                                      </select>
                                    </div>
                                  </td>

                                  <td className="col-tax">
                                    <select
                                      className="form-select form-select-sm"
                                      value={taxType}
                                      onChange={(e) =>
                                        updateItem(idx, {
                                          taxType: e.target.value,
                                        })
                                      }
                                    >
                                      <option value="exclusive">
                                        Exclusive ({TAX_PERCENT}%)
                                      </option>
                                      <option value="inclusive">
                                        Inclusive (0%)
                                      </option>
                                    </select>
                                  </td>

                                  <td className="text-end">
                                    ${taxAmt.toFixed(2)}
                                  </td>
                                  <td className="text-end">
                                    ${price.toFixed(2)}
                                  </td>
                                  <td className="text-end">
                                    ${totalCost.toFixed(2)}
                                  </td>

                                  <td className="col-actions text-center">
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-icon btn-sm"
                                      onClick={() => removeItem(idx)}
                                      title="Remove"
                                    >
                                      ×
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="row">
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Customer Name
                            <span className="text-danger ms-1">*</span>
                          </label>
                          <div className="row">
                            <div className="col-lg-10 col-sm-10 col-10">
                              <Select
                                classNamePrefix="react-select"
                                options={customerOptions}
                                placeholder="Choose"
                                onChange={(selected) => {
                                  if (selected) {
                                    console.log("Selected Customer:", selected);
                                    handleInputChange(
                                      "customer",
                                      selected.value
                                    ); // ✅ save the ID
                                  } else {
                                    handleInputChange("customer", ""); // or null
                                  }
                                }}
                              />
                            </div>
                            <div className="col-lg-2 col-sm-2 col-2 ps-0">
                              <div className="add-icon">
                                <Link
                                  to="#"
                                  className="bg-dark text-white p-2 rounded"
                                >
                                  <PlusCircle />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Date<span className="text-danger ms-1">*</span>
                          </label>
                          <div className="input-groupicon calender-input">
                            <DatePicker
                              className="form-control datetimepicker"
                              placeholder="dd/mm/yyyy"
                              value={
                                formData.date ? dayjs(formData.date) : null
                              } // use dayjs or moment
                              onChange={(date, dateString) =>
                                handleInputChange("date", dateString)
                              }
                            />
                            <Calendar className="info-img" />
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Supplier<span className="text-danger ms-1">*</span>
                          </label>
                          <Select
                            classNamePrefix="react-select"
                            options={supplierOptions}
                            placeholder="Choose"
                            onChange={(selected) => {
                              if (selected) {
                                handleInputChange("supplier", selected.value); // ✅ save the ID
                              } else {
                                handleInputChange("supplier", "");
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Product<span className="text-danger ms-1">*</span>
                          </label>
                          <div className="input-groupicon select-code">
                            <Select
                              classNamePrefix="react-select"
                              options={productList}
                              placeholder="Search by product name or code"
                              getOptionValue={(option) => option.value} // <-- ensures value exists
                              getOptionLabel={(option) => option.label}
                              onChange={(selected) => {
                                const matched = productList.find(
                                  (p) => p.value === selected?.value
                                );

                                if (!matched || !matched.fullProduct) {
                                  // toast.error("Product selection is invalid");
                                  toast.error(`❌ ${window.translate("Product selection is invalid")}`);
                                  return;
                                }

                                const product = matched.fullProduct;

                                handleAddProduct({
                                  product: selected.value, // now will be _id
                                  quantity: 1,
                                  price: parseFloat(
                                    product?.Price || product?.price || 0
                                  ),
                                  discountValue: product?.discountValue ?? 0,
                                  discountType: product?.discountType ?? "flat",
                                  taxType:
                                    product?.tax === "inclusive" ||
                                    product?.tax === "exclusive"
                                      ? product.tax
                                      : "exclusive",
                                });
                              }}
                            />
                            <div className="addonset">
                              <ImageWithBasePath
                                src="assets/img/icons/qrcode-scan.svg"
                                alt="img"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-6 ms-auto">
                        <div className="total-order w-100 max-widthauto m-auto mb-4">
                          <ul className="border-1 rounded-2">
                            <li className="border-bottom">
                              <h4 className="border-end">Price</h4>
                              <h5>$ {formData.total}</h5>
                            </li>
                            <li className="border-bottom">
                              <h4 className="border-end">Order Tax</h4>
                              <h5>$ {formData.orderTax}</h5>
                            </li>
                            <li className="border-bottom">
                              <h4 className="border-end">Discount</h4>
                              <h5>$ {formData.discount}</h5>
                            </li>
                            <li className="border-bottom">
                              <h4 className="border-end">Shipping</h4>
                              <h5>$ {formData.shipping}</h5>
                            </li>
                            <li className="border-bottom">
                              <h4 className="border-end">Grand Total</h4>
                              <h5>$ {formData.grandTotal}</h5>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Order Tax<span className="text-danger ms-1">*</span>
                          </label>
                          <div className="input-groupicon select-code">
                            <input
                              type="number"
                              value={formData.orderTax}
                              className="form-control p-2"
                              onChange={(e) => {
                                const orderTaxVal = parseFloat(
                                  e.target.value || 0
                                );
                                setFormData((prev) => {
                                  const updated = {
                                    ...prev,
                                    orderTax: orderTaxVal,
                                  };
                                  return {
                                    ...updated,
                                    grandTotal: recalculateTotals(updated),
                                  };
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Discount<span className="text-danger ms-1">*</span>
                          </label>
                          <div className="input-groupicon select-code">
                            <input
                              type="number"
                              value={formData.discount}
                              className="form-control p-2"
                              onChange={(e) => {
                                const discountVal = parseFloat(
                                  e.target.value || 0
                                );
                                setFormData((prev) => {
                                  const updated = {
                                    ...prev,
                                    discount: discountVal,
                                  };
                                  return {
                                    ...updated,
                                    grandTotal: recalculateTotals(updated),
                                  };
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Shipping<span className="text-danger ms-1">*</span>
                          </label>
                          <div className="input-groupicon select-code">
                            <input
                              type="text"
                              value={formData.shipping}
                              className="form-control p-2"
                              onChange={(e) => {
                                const shippingVal = parseFloat(
                                  e.target.value || 0
                                );
                                setFormData((prev) => {
                                  const updated = {
                                    ...prev,
                                    shipping: shippingVal,
                                  };
                                  return {
                                    ...updated,
                                    grandTotal: recalculateTotals(updated),
                                  };
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3 mb-5">
                          <label className="form-label">
                            Status<span className="text-danger ms-1">*</span>
                          </label>
                          <Select
                            classNamePrefix="react-select"
                            options={OrderStatus}
                            placeholder="Choose"
                            onChange={(selected) =>
                              handleInputChange("status", selected.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Paid<span className="text-danger ms-1">*</span>
                          </label>
                          <input
                            type="number"
                            className="form-control p-2"
                            value={formData.paid}
                            onChange={(e) => {
                              const paidAmount = parseFloat(
                                e.target.value || 0
                              );
                              const grand = parseFloat(
                                formData.grandTotal || 0
                              );

                              if (paidAmount > grand) {
                                toast.warning(
                                  "Paid amount cannot exceed grand total"
                                );
                                return;
                              }

                              let paymentStatus = "Unpaid";
                              if (paidAmount === 0) paymentStatus = "Unpaid";
                              else if (paidAmount < grand)
                                paymentStatus = "Overdue";
                              else paymentStatus = "Paid";

                              setFormData((prev) => ({
                                ...prev,
                                paid: paidAmount,
                                paymentStatus,
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary add-cancel me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary add-sale"
                    onClick={handleSubmit}
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /add popup */}

        {/* details popup */}
        <div className="modal fade" id="sales-details-new">
          <div className="modal-dialog sales-details-modal">
            <div className="modal-content">
              <div className="page-header p-4 border-bottom mb-0">
                <div className="add-item d-flex align-items-center">
                  <div className="page-title modal-datail">
                    <h4 className="mb-0 me-2">{window.translate("Sales Detail")}</h4>
                  </div>
                </div>
                <ul className="table-top-head">
                  <li>
                    <Link
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Pdf"
                    >
                      <ImageWithBasePath
                        src="assets/img/icons/pdf.svg"
                        alt="img"
                      />
                    </Link>
                  </li>
                  <li>
                    <Link
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Pdf"
                    >
                      <ImageWithBasePath
                        src="assets/img/icons/printer.svg"
                        alt="img"
                      />
                    </Link>
                  </li>
                </ul>
                <div className="page-btn">
                  <Link
                    to="#"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    <ArrowLeft className="me-2" /> {window.translate("Back to Sales")}
                  </Link>
                </div>
              </div>
              <form>
                <div className="card border-0">
                  <div className="card-body pb-0">
                    <div
                      className="invoice-box table-height"
                      style={{
                        maxWidth: 1600,
                        width: "100%",
                        padding: 0,
                        fontSize: 14,
                        color: "#555",
                      }}
                    >
                      <div className="row sales-details-items d-flex">
                        <div className="col-md-4 details-item">
                          <h6>{window.translate("Customer Info")}</h6>
                          <h4 className="mb-1">
                            {salesDetails?.customer
                              ? `${salesDetails.customer.firstName?.en ?? ""} ${
                                  salesDetails.customer.lastName?.en ?? ""
                                }`.trim()
                              : "—"}
                          </h4>
                          <p className="mb-0">
                           {window.translate("Address")}: {salesDetails?.customer?.address?.en}
                          </p>
                          <p className="mb-0">
                            {window.translate("Email")}: 
                            <span> {salesDetails?.customer?.email ?? "—"}</span>
                          </p>
                          <p className="mb-0">
                            {window.translate("Phone")}: 
                            <span> {salesDetails?.customer?.phone ?? "—"}</span>
                          </p>
                        </div>
                        
                        <div className="col-md-4 details-item">
                          <h6>{window.translate("Invoice Info")}</h6>
                          <p className="mb-0">
                           {window.translate("Reference")}:
                            <span className="fs-16 text-primary ms-2">
                              {salesDetails?.reference}
                            </span>
                          </p>
                          <p className="mb-0">
                            {window.translate("Date")}:
                            <span className="ms-2 text-gray-9">
                              {salesDetails?.date
                                ? new Date(
                                    salesDetails.date
                                  ).toLocaleDateString()
                                : "--"}
                            </span>
                          </p>
                          <p className="mb-0">
                            {window.translate("Status")}:
                            <span className="badge badge-success ms-2">
                              {salesDetails?.status}
                            </span>
                          </p>
                          <p className="mb-0">
                            {window.translate("Payment Status")}:
                            <span className="badge badge-soft-success shadow-none badge-xs d-inline-flex align-items-center ms-2">
                              <i className="ti ti-point-filled" />
                              {salesDetails?.paymentStatus}
                            </span>
                          </p>
                        </div>
                      </div>
                      <h5 className="order-text">{window.translate("Order Summary")}</h5>
                      <div className="table-responsive no-pagination mb-3">
                        <table className="table datanew">
                          <thead>
                            <tr>
                              <th>{window.translate("Product")}</th>
                              <th>{window.translate("Purchase Price($)")}</th>
                              <th>{window.translate("Discount($)")}</th>
                              <th>{window.translate("Tax(%)")}</th>
                              <th>{window.translate("Tax Amount($)")}</th>
                              <th>{window.translate("Unit Cost($)")}</th>
                              <th>{window.translate("Total Cost($)")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {salesDetails?.items?.length > 0 ? (
                              salesDetails.items.map((item, index) => {
                                const product = item?.product || {};
                                const name = getProductName(product);
                                const img = getProductImage(product);

                                const price = parseFloat(item?.price ?? 0);
                                const qty = parseFloat(item?.quantity ?? 0);
                                const discount = parseFloat(
                                  item?.discountValue ?? item?.discount ?? 0
                                ); // fallback
                                const taxRate =
                                  item?.taxType === "exclusive"
                                    ? TAX_PERCENT
                                    : 0;
                                const taxAmount =
                                  ((price * qty - discount) * taxRate) / 100;
                                const totalCost =
                                  price * qty - discount + taxAmount;

                                return (
                                  <tr key={index}>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <Link
                                          to="#"
                                          className="avatar avatar-md me-2"
                                        >
                                          <ProductImage src={img} alt={name} />
                                        </Link>
                                        <Link to="#">{name}</Link>
                                      </div>
                                    </td>
                                    <td>{price.toFixed(2)}</td>
                                    <td>{discount.toFixed(2)}</td>
                                    <td>{taxRate.toFixed(2)}</td>
                                    <td>{taxAmount.toFixed(2)}</td>
                                    <td>{price.toFixed(2)}</td>
                                    <td>{totalCost.toFixed(2)}</td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center">
                                  No items found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="row">
                      <div className="row">
                        <div className="col-lg-6 ms-auto">
                          <div className="total-order w-100 max-widthauto m-auto mb-4">
                            <ul className="border-1 rounded-1">
                              <li className="border-bottom">
                                <h4 className="border-end">Order Tax</h4>
                                <h5>$ {(salesDetails?.tax || 0).toFixed(2)}</h5>
                              </li>
                              <li className="border-bottom">
                                <h4 className="border-end">Discount</h4>
                                <h5>
                                  $ {(salesDetails?.discount || 0).toFixed(2)}
                                </h5>
                              </li>
                              <li className="border-bottom">
                                <h4 className="border-end">Grand Total</h4>
                                <h5>
                                  $ {(salesDetails?.total || 0).toFixed(2)}
                                </h5>
                              </li>
                              <li className="border-bottom">
                                <h4 className="border-end">Paid</h4>
                                <h5>
                                  $ {(salesDetails?.paid || 0).toFixed(2)}
                                </h5>
                              </li>
                              <li className="border-bottom">
                                <h4 className="border-end">Due</h4>
                                <h5>
                                  $
                                  {Math.abs(
                                    (salesDetails?.total || 0) -
                                      (salesDetails?.paid || 0)
                                  ) < 0.01
                                    ? "0.00"
                                    : (
                                        (salesDetails?.total || 0) -
                                        (salesDetails?.paid || 0)
                                      ).toFixed(2)}
                                </h5>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Submit
                  </button>
                </div> */}
              </form>
            </div>
          </div>
        </div>
        {/* /details popup */}

        {/* edit popup */}
        <div className="modal fade" id="edit-sales-new">
          <div className="modal-dialog edit-sales-modal">
            <div className="modal-content">
              <div className="page-wrapper p-0 m-0">
                <div className="content p-0">
                  <div className="page-header p-4 mb-0">
                    <div className="add-item new-sale-items d-flex">
                      <div className="page-title">
                        <h4>Edit Sales</h4>
                      </div>
                      <button
                        type="button"
                        className="close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">×</span>
                      </button>
                    </div>
                  </div>
                  <form>
                    <div className="card border-0">
                      <div className="card-body pb-0">
                        <div className="table-responsive no-pagination mb-3">
                          <table className="table  datanew">
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th>Qty</th>
                                <th>Purchase Price($)</th>
                                <th>Discount($)</th>
                                <th>Tax(%)</th>
                                <th>Tax Amount($)</th>
                                <th>Unit Cost($)</th>
                                <th>Total Cost(%)</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Link
                                      to="#"
                                      className="avatar avatar-md me-2"
                                    >
                                      <ImageWithBasePath
                                        src="assets/img/products/stock-img-02.png"
                                        alt="product"
                                      />
                                    </Link>
                                    <Link to="#">Nike Jordan</Link>
                                  </div>
                                </td>
                                <td>
                                  <div className="product-quantity bg-gray-transparent border-0">
                                    <span className="quantity-btn">
                                      +
                                      <PlusCircle className="plus-circle" />
                                      onClick={handleIncrement}
                                    </span>
                                    <input
                                      type="text"
                                      className="quntity-input form-control bg-transparent"
                                      defaultValue={2}
                                    />
                                    <span className="quantity-btn">
                                      <Minus className="feather-search" />
                                      onClick={handleDecrement}
                                    </span>
                                  </div>
                                </td>
                                <td>2000</td>
                                <td>500</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>1500</td>
                              </tr>
                              <tr>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Link
                                      to="#"
                                      className="avatar avatar-md me-2"
                                    >
                                      <ImageWithBasePath
                                        src="assets/img/products/stock-img-03.png"
                                        alt="product"
                                      />
                                    </Link>
                                    <Link to="#">Apple Series 5 Watch</Link>
                                  </div>
                                </td>
                                <td>
                                  <div className="product-quantity bg-gray-transparent border-0">
                                    <span className="quantity-btn">
                                      +
                                      <PlusCircle className="plus-circle" />
                                      onClick={handleIncrement}
                                    </span>
                                    <input
                                      type="text"
                                      className="quntity-input form-control bg-transparent"
                                      defaultValue={2}
                                    />
                                    <span className="quantity-btn">
                                      <Minus className="feather-search" />
                                      onClick={handleDecrement}
                                    </span>
                                  </div>
                                </td>
                                <td>3000</td>
                                <td>400</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>1700</td>
                              </tr>
                              <tr>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Link
                                      to="#"
                                      className="avatar avatar-md me-2"
                                    >
                                      <ImageWithBasePath
                                        src="assets/img/products/stock-img-05.png"
                                        alt="product"
                                      />
                                    </Link>
                                    <Link to="#">Lobar Handy</Link>
                                  </div>
                                </td>
                                <td>
                                  <div className="product-quantity bg-gray-transparent border-0">
                                    <span className="quantity-btn">
                                      +
                                      <PlusCircle className="plus-circle" />
                                      onClick = {handleIncrement}
                                    </span>
                                    <input
                                      type="text"
                                      className="quntity-input form-control bg-transparent"
                                      defaultValue={2}
                                    />
                                    <span className="quantity-btn">
                                      <Minus className="feather-search" />
                                      onClick = {handleDecrement}
                                    </span>
                                  </div>
                                </td>
                                <td>2500</td>
                                <td>500</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>0.00</td>
                                <td>2000</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="row">
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Customer Name
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="row">
                                <div className="col-lg-10 col-sm-10 col-10">
                                  <Select
                                    classNamePrefix="react-select"
                                    options={CustomerName}
                                    placeholder="Choose"
                                  />
                                </div>
                                <div className="col-lg-2 col-sm-2 col-2 ps-0">
                                  <div className="add-icon">
                                    <Link
                                      to="#"
                                      className="bg-dark text-white p-2 rounded"
                                    >
                                      <PlusCircle className="plus" />
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Date<span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon calender-input">
                                <DatePicker
                                  className="form-control datetimepicker"
                                  placeholder="dd/mm/yyyy"
                                />
                                <Calendar className="info-img" />
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Supplier
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <Select
                                classNamePrefix="react-select"
                                options={Supplier}
                                placeholder="Choose"
                              />
                            </div>
                          </div>
                          <div className="col-lg-12 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Product Name
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon select-code">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Please type product code and select"
                                />
                                <div className="addonset">
                                  <ImageWithBasePath
                                    src="assets/img/icons/scanners.svg"
                                    alt="img"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6 ms-auto">
                            <div className="total-order w-100 max-widthauto m-auto mb-4">
                              <ul className="border-1 rounded-1">
                                <li className="border-bottom">
                                  <h4 className="border-end">Order Tax</h4>
                                  <h5>$ 0.00</h5>
                                </li>
                                <li className="border-bottom">
                                  <h4 className="border-end">Discount</h4>
                                  <h5>$ 0.00</h5>
                                </li>
                                <li className="border-bottom">
                                  <h4 className="border-end">Shipping</h4>
                                  <h5>$ 0.00</h5>
                                </li>
                                <li className="border-bottom">
                                  <h4 className="border-end">Grand Total</h4>
                                  <h5>$5200.00</h5>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Order Tax
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon select-code">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={0}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Discount
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon select-code">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={0}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Shipping
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <div className="input-groupicon select-code">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={0}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-lg-3 col-sm-6 col-12">
                            <div className="mb-3 mb-5">
                              <label className="form-label">
                                Status
                                <span className="text-danger ms-1">*</span>
                              </label>
                              <Select
                                classNamePrefix="react-select"
                                options={OrderStatus}
                                placeholder="Choose"
                              />
                            </div>
                          </div>
                          <div className="col-lg-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Notes<span className="text-danger ms-1">*</span>
                              </label>
                              <DefaultEditor
                                value={values}
                                onChange={onChange}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary add-cancel me-3"
                        data-bs-dismiss="modal"
                      >
                        {window.translate("cancel")}
                      </button>
                      <Link
                        to="#"
                        className="btn btn-primary add-sale"
                        data-bs-dismiss="modal"
                      >
                        Save Changes
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /edit popup */}
        {/* show payment Modal */}
        <div
          className="modal fade"
          id="showpayment"
          tabIndex={-1}
          aria-labelledby="showpayment"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered stock-adjust-modal">
            <div className="modal-content">
              <div className="modal-header">
                <div className="page-title">
                  <h4>Show Payments</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="modal-body-table total-orders p-0">
                      <div className="table-responsive rounded">
                        <table className="table datatable">
                          <thead className="thead-light">
                            <tr>
                              <th>Date</th>
                              <th>Reference</th>
                              <th>Amount</th>
                              <th>Paid By</th>
                              <th className="no-sort" />
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>19 Jan 2023</td>
                              <td>INV/SL0101</td>
                              <td>$1500</td>
                              <td>Cash</td>
                              <td>
                                <div className="edit-delete-action d-flex align-items-center">
                                  <Link
                                    className="me-3 p-2 border rounded d-flex align-items-center"
                                    to="#"
                                  >
                                    <i
                                      data-feather="printer"
                                      className="feather-rotate-ccw"
                                    />
                                  </Link>
                                  <Link
                                    className="me-3 p-2 border rounded d-flex align-items-center"
                                    to="#"
                                    data-bs-toggle="modal"
                                    data-bs-target="#editpayment"
                                  >
                                    <i
                                      data-feather="edit"
                                      className="feather-edit"
                                    />
                                  </Link>
                                  <Link
                                    className="p-2 border rounded d-flex align-items-center"
                                    to="#"
                                  >
                                    <i
                                      data-feather="trash-2"
                                      className="feather-trash-2"
                                    />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* show payment Modal */}
        {/* Create payment Modal */}
        <div
          className="modal fade"
          id="createpayment"
          tabIndex={-1}
          aria-labelledby="createpayment"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div className="page-title">
                  <h4>Create Payments</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <form action="online-orders.html">
                <div className="modal-body">
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          {" "}
                          Date<span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon calender-input">
                          <DatePicker
                            className="form-control datetimepicker"
                            placeholder="dd/mm/yyyy"
                          />
                          <Calendar className="info-img" />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Reference<span className="text-danger ms-1">*</span>
                        </label>
                        <input type="text" className="form-control" />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Received Amount
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon doller-input">
                          <DollarSign className="info-img" />
                          <input type="text" className="form-control ps-4" />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Paying Amount
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon doller-input">
                          <DollarSign className="info-img" />
                          <input type="text" className="form-control ps-4" />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Payment type
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <Select
                          classNamePrefix="react-select"
                          options={PaymentType}
                          placeholder="Choose"
                        />
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("description")}
                        </label>
                        <DefaultEditor value={values} onChange={onChange} />
                        <p>Maximum 60 Characters</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <Link
                    to="#"
                    className="btn btn-primary"
                    data-bs-dismiss="modal"
                  >
                    Submit
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* Create payment Modal */}
        {/* edit payment Modal */}
        <div
          className="modal fade"
          id="editpayment"
          tabIndex={-1}
          aria-labelledby="editpayment"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div className="page-title">
                  <h4>Edit Payments</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <form action="online-orders.html">
                <div className="modal-body">
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Date<span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon calender-input">
                          <i data-feather="calendar" className="info-img" />
                          <input
                            type="text"
                            className="datetimepicker form-control"
                            placeholder="Select Date"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Reference<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          defaultValue="INV/SL0101"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Received Amount
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon calender-input">
                          <i data-feather="dollar-sign" className="info-img" />
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={1500}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Paying Amount
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-groupicon calender-input">
                          <i data-feather="dollar-sign" className="info-img" />
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={1500}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-12 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Payment type
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <select className="select">
                          <option>Cash</option>
                          <option>Online</option>
                          <option>Inprogress</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3 summer-description-box transfer">
                        <label className="form-label">
                          {window.translate("description")}
                        </label>
                        <div id="summernote2" />
                      </div>
                      <p>Maximum 60 Characters</p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* edit payment Modal */}
      </>
    </div>
  );
};

OnlineorderModal.propTypes = {
  salesDetails: PropTypes.shape({
    _id: PropTypes.string.isRequired,

    reference: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    status: PropTypes.string,
    paymentStatus: PropTypes.string,
    total: PropTypes.number,
    paid: PropTypes.number,
    tax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    discount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    customer: PropTypes.shape({
      _id: PropTypes.string,
      firstName: PropTypes.shape({
        en: PropTypes.string,
        ar: PropTypes.string,
      }),
      lastName: PropTypes.shape({
        en: PropTypes.string,
        ar: PropTypes.string,
      }),
      address: PropTypes.shape({
        en: PropTypes.string,
        ar: PropTypes.string,
      }),
      city: PropTypes.shape({
        en: PropTypes.string,
        ar: PropTypes.string,
      }),
      state: PropTypes.shape({
        en: PropTypes.string,
        ar: PropTypes.string,
      }),
      country: PropTypes.shape({
        en: PropTypes.string,
        ar: PropTypes.string,
      }),
      email: PropTypes.string,
      phone: PropTypes.string,
      postalCode: PropTypes.string,
      image: PropTypes.string, // base64 data URL in your example
    }),

    // If your order includes line items, keep this flexible:
    items: PropTypes.arrayOf(
      PropTypes.shape({
        product: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        variant: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        quantity: PropTypes.number,
        price: PropTypes.number,
        discountValue: PropTypes.number,
        taxType: PropTypes.string,
      })
    ),
  }),
};

OnlineorderModal.defaultProps = {
  salesDetails: null,
};

export default OnlineorderModal;
